import { useState, useEffect } from "react";
import { apiFetch } from "../App";

const BOOKMAKER_LABELS = {
  betway_bw: 'Betway', pstbets_bw: 'PST Bets', sunbet_bw: 'Sunbet',
  bet267_bw: 'Bet267', bbets_bw: 'BBets', betxplosion_bw: 'BetXplosion',
};

function MiniCard({ label, value, sub, color }) {
  return (
    <div className="card" style={{ padding: 14 }}>
      <div style={{ fontSize: 10, color: "var(--text3)", textTransform: "uppercase", letterSpacing: .5, marginBottom: 6 }}>{label}</div>
      <div style={{ fontFamily: "var(--font-display)", fontWeight: 700, fontSize: 18, color: color || "var(--text)" }}>{value}</div>
      {sub && <div style={{ fontSize: 11, color: "var(--text3)", marginTop: 3 }}>{sub}</div>}
    </div>
  );
}

function KellyResult({ label, value, color, note }) {
  return (
    <div style={{ background: "var(--bg3)", borderRadius: "var(--radius-sm)", padding: "12px 10px", textAlign: "center" }}>
      <div style={{ fontFamily: "var(--font-display)", fontWeight: 800, fontSize: 20, color }}>{value}</div>
      <div style={{ fontSize: 12, fontWeight: 600, marginTop: 4 }}>{label}</div>
      <div style={{ fontSize: 10, color: "var(--text3)", marginTop: 2 }}>{note}</div>
    </div>
  );
}

function ResultPill({ result }) {
  const map = { won: ["pill-green", "WON"], lost: ["pill-red", "LOST"], pending: ["pill-blue", "PENDING"], void: ["pill-amber", "VOID"] };
  const [cls, label] = map[result] || ["pill-blue", result];
  return <span className={`pill ${cls}`} style={{ fontSize: 10 }}>{label}</span>;
}

export default function BankrollManager() {
  const [bankroll, setBankroll] = useState(null);
  const [history, setHistory] = useState([]);
  //const [parlay, setParlay] = useState(null);
  const [loading, setLoading] = useState(true);
  //const [parlayStake, setParlayStake] = useState(20);
  //const [riskLevel, setRiskLevel] = useState('balanced');
  //const [parlayLoading, setParlayLoading] = useState(false);
  const [kelly, setKelly] = useState({ odds: "2.00", prob: "55", result: null });
  const [deposit, setDeposit] = useState({ amount: "", note: "" });
  const [tab, setTab] = useState("overview");
  const [kellyOpen, setKellyOpen] = useState(false);
  const [width, setWidth] = useState(window.innerWidth);

  useEffect(() => {
    const handler = () => setWidth(window.innerWidth);
    window.addEventListener('resize', handler);
    return () => window.removeEventListener('resize', handler);
  }, []);

  const isMobile = width < 768;

  useEffect(() => {
    Promise.all([apiFetch("/bankroll"), apiFetch("/bankroll/history?limit=20")])
      .then(([br, hist]) => { setBankroll(br); setHistory(hist.bets || []); })
      .catch(console.error)
      .finally(() => setLoading(false));
  }, []);

  const calcKelly = async () => {
    try {
      const res = await apiFetch(`/bankroll/kelly?odds=${kelly.odds}&prob=${kelly.prob}&bankroll=${bankroll?.currentAmount || 100}`);
      setKelly(k => ({ ...k, result: res }));
    } catch (e) { console.error(e); }
  };

  const buildParlay = async () => {
  setParlayLoading(true);
  setParlay(null);
  try {
    const data = await apiFetch('/smart-parlay', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ stake: parlayStake, riskLevel }),
    });
    setParlay(data);
  } catch (e) { console.error(e); }
  finally { setParlayLoading(false); }
};

  const handleDeposit = async () => {
    if (!deposit.amount) return;
    try {
      await apiFetch("/bankroll/deposit", { method: "POST", body: JSON.stringify({ amount: parseFloat(deposit.amount), note: deposit.note }) });
      const br = await apiFetch("/bankroll");
      setBankroll(br);
      setDeposit({ amount: "", note: "" });
    } catch (e) { console.error(e); }
  };

  const health = bankroll?.healthScore ?? 100;
  const healthColor = health > 70 ? "var(--success)" : health > 40 ? "var(--warning)" : "var(--danger)";
  const healthLabel = health > 70 ? "Healthy" : health > 40 ? "Caution" : "At Risk";

  const TABS = ["overview", "kelly", "history"];

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>

      {/* Tab bar — scrollable on mobile */}
      <div style={{ display: "flex", gap: 0, borderBottom: "1px solid var(--border)", overflowX: "auto" }}>
        {TABS.map(t => (
          <button key={t} onClick={() => setTab(t)} style={{
            padding: "9px 16px", whiteSpace: "nowrap",
            fontFamily: "var(--font-display)", fontWeight: 600, fontSize: 12,
            textTransform: "uppercase", letterSpacing: .5,
            color: tab === t ? "var(--accent)" : "var(--text3)",
            background: "transparent", border: "none",
            borderBottom: tab === t ? "2px solid var(--accent)" : "2px solid transparent",
            cursor: "pointer", transition: "all .15s", flexShrink: 0,
          }}>
            {t}
          </button>
        ))}
      </div>

      {/* OVERVIEW */}
      {tab === "overview" && (
        <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
          {loading ? <div className="skeleton" style={{ height: 160 }} /> : (
            <>
              {/* Main bankroll card — Fix: not cramped on mobile */}
              <div className="card" style={{ padding: isMobile ? 16 : 24 }}>
                <div style={{ marginBottom: 16 }}>
                  <div style={{ fontSize: 10, color: "var(--text3)", textTransform: "uppercase", letterSpacing: 1, marginBottom: 6 }}>Current Bankroll</div>
                  <div style={{ fontFamily: "var(--font-display)", fontWeight: 800, fontSize: isMobile ? 36 : 48, color: "var(--accent)", lineHeight: 1 }}>
                    P {bankroll?.currentAmount?.toFixed(2) ?? "0.00"}
                  </div>
                  <div style={{ fontSize: 12, color: "var(--text3)", marginTop: 6 }}>
                    Started at P{bankroll?.startingAmount?.toFixed(2)} ·{" "}
                    <span style={{ color: bankroll?.allTimePL >= 0 ? "var(--success)" : "var(--danger)" }}>
                      {bankroll?.allTimePL >= 0 ? "+" : ""}P{bankroll?.allTimePL?.toFixed(2)} all time
                    </span>
                  </div>
                </div>

                {/* Health score */}
                <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 16, padding: "10px 12px", background: "var(--bg3)", borderRadius: "var(--radius-sm)" }}>
                  <div style={{ fontFamily: "var(--font-display)", fontWeight: 800, fontSize: 28, color: healthColor, flexShrink: 0 }}>{health}</div>
                  <div>
                    <div style={{ fontSize: 12, fontWeight: 600, color: healthColor }}>{healthLabel}</div>
                    <div style={{ fontSize: 11, color: "var(--text3)" }}>Health score / 100</div>
                  </div>
                  <span className={`pill ${health > 70 ? "pill-green" : health > 40 ? "pill-amber" : "pill-red"}`} style={{ marginLeft: "auto" }}>
                    {healthLabel}
                  </span>
                </div>

                {bankroll?.healthFlags?.length > 0 && (
                  <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginBottom: 12 }}>
                    {bankroll.healthFlags.map(f => (
                      <span key={f} className="pill pill-red" style={{ fontSize: 10 }}>⚠️ {f.replace(/_/g, " ")}</span>
                    ))}
                  </div>
                )}

                <div>
                  <div style={{ display: "flex", justifyContent: "space-between", fontSize: 11, color: "var(--text3)", marginBottom: 6 }}>
                    <span>Monthly target progress</span>
                    <span>P{bankroll?.monthlyPL?.toFixed(0)} / P{((bankroll?.startingAmount || 0) * ((bankroll?.monthlyTargetPct || 10) / 100)).toFixed(0)}</span>
                  </div>
                  <div className="progress">
                    <div className="progress-fill" style={{ width: `${Math.min(100, Math.max(0, (bankroll?.monthlyPL / ((bankroll?.startingAmount || 1) * ((bankroll?.monthlyTargetPct || 10) / 100))) * 100))}%` }} />
                  </div>
                </div>
              </div>

              {/* Metrics */}
              <div style={{ display: "grid", gridTemplateColumns: "repeat(3,1fr)", gap: 10 }}>
                <MiniCard label="Max Stake" value={`P${((bankroll?.currentAmount || 0) * 0.05).toFixed(2)}`} sub="5% rule" />
                <MiniCard label="Monthly P&L" value={`${bankroll?.monthlyPL >= 0 ? "+" : ""}P${bankroll?.monthlyPL?.toFixed(0)}`} color={bankroll?.monthlyPL >= 0 ? "var(--success)" : "var(--danger)"} />
                <MiniCard label="Total Bets" value={history.length} sub="this month" />
              </div>
            </>
          )}

          {/* Adjust bankroll — Fix: stacked on mobile */}
          <div className="card">
            <div className="section-header"><div className="section-title">Adjust Bankroll</div></div>
            <div style={{ padding: 16, display: "flex", flexDirection: isMobile ? "column" : "row", gap: 10 }}>
              <input className="input-field" type="number" placeholder="Amount (BWP)" value={deposit.amount} onChange={e => setDeposit(d => ({ ...d, amount: e.target.value }))} style={{ flex: 1 }} />
              <input className="input-field" placeholder="Note (optional)" value={deposit.note} onChange={e => setDeposit(d => ({ ...d, note: e.target.value }))} style={{ flex: 2 }} />
              <div style={{ display: "flex", gap: 8 }}>
                <button className="btn btn-primary" style={{ flex: 1 }} onClick={handleDeposit}>+ Deposit</button>
                <button className="btn btn-ghost" style={{ flex: 1 }} onClick={async () => {
                  if (!deposit.amount) return;
                  await apiFetch("/bankroll/withdraw", { method: "POST", body: JSON.stringify({ amount: parseFloat(deposit.amount) }) });
                  const br = await apiFetch("/bankroll");
                  setBankroll(br);
                  setDeposit({ amount: "", note: "" });
                }}>− Withdraw</button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* KELLY — Fix: stacked on mobile, primer as accordion */}
      {tab === "kelly" && (
        <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
          <div className="card">
            <div className="section-header"><div className="section-title">🧮 Kelly Criterion Calculator</div></div>
            <div style={{ padding: 16, display: "flex", flexDirection: "column", gap: 14 }}>
              <div style={{ fontSize: 13, color: "var(--text3)", lineHeight: 1.6 }}>
                Enter the decimal odds and your estimated win probability to calculate how much you should stake.
              </div>
              <div style={{ display: "grid", gridTemplateColumns: isMobile ? "1fr" : "1fr 1fr", gap: 12 }}>
                <div className="input-group">
                  <label className="input-label">Decimal Odds</label>
                  <input className="input-field" type="number" step="0.01" value={kelly.odds} onChange={e => setKelly(k => ({ ...k, odds: e.target.value }))} placeholder="e.g. 2.10" />
                </div>
                <div className="input-group">
                  <label className="input-label">Win Probability %</label>
                  <input className="input-field" type="number" step="1" min="1" max="99" value={kelly.prob} onChange={e => setKelly(k => ({ ...k, prob: e.target.value }))} placeholder="e.g. 55" />
                </div>
              </div>
              <div className="input-group">
                <label className="input-label">Bankroll (BWP) — your current bankroll</label>
                <input className="input-field" type="number" value={bankroll?.currentAmount || 100} disabled style={{ opacity: .6 }} />
              </div>
              <button className="btn btn-primary" onClick={calcKelly}>Calculate Kelly Stakes</button>

              {kelly.result && (
                <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
                  {kelly.result.hasEdge ? (
                    <>
                      <div style={{ fontSize: 12, color: "var(--text3)", padding: "10px 14px", background: "var(--bg3)", borderRadius: "var(--radius-sm)" }}>
                        Kelly fraction: <strong style={{ color: "var(--text)" }}>{(kelly.result.kellyFraction * 100).toFixed(2)}%</strong> of bankroll
                      </div>
                      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 10 }}>
                        <KellyResult label="Full Kelly" value={`P${kelly.result.fullKelly}`} color="var(--danger)" note="Aggressive" />
                        <KellyResult label="Half Kelly" value={`P${kelly.result.halfKelly}`} color="var(--accent)" note="Recommended ✓" />
                        <KellyResult label="Quarter Kelly" value={`P${kelly.result.quarterKelly}`} color="var(--accent2)" note="Conservative" />
                      </div>
                    </>
                  ) : (
                    <div style={{ padding: "14px 16px", background: "rgba(240,71,71,0.08)", border: "1px solid rgba(240,71,71,0.2)", borderRadius: "var(--radius-sm)", fontSize: 13, color: "var(--danger)" }}>
                      ❌ No edge at these odds and probability. Do not bet.
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>

          {/* Kelly Primer — Fix: accordion instead of always visible */}
          <div className="card" style={{ padding: 0, overflow: 'hidden' }}>
            <button
              onClick={() => setKellyOpen(o => !o)}
              style={{ width: '100%', display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '14px 20px', background: 'transparent', border: 'none', cursor: 'pointer', textAlign: 'left' }}
            >
              <div style={{ fontFamily: "var(--font-display)", fontWeight: 700, fontSize: 14 }}>📚 What is Kelly Criterion?</div>
              <span style={{ color: 'var(--text3)', transform: kellyOpen ? 'rotate(180deg)' : 'none', transition: 'transform .2s' }}>↓</span>
            </button>
            {kellyOpen && (
              <div style={{ padding: "0 20px 20px", display: "flex", flexDirection: "column", gap: 10 }}>
                {[
                  { icon: "🎯", title: "What is Kelly?", body: "The Kelly Criterion calculates the optimal fraction of your bankroll to stake on a bet to maximise long-run growth." },
                  { icon: "⚠️", title: "Why Half Kelly?", body: "Full Kelly assumes perfect edge estimation. Use Half Kelly — it gives 75% of Full Kelly's growth with half the variance." },
                  { icon: "📈", title: "Positive edge only", body: "If Kelly returns a negative number, you have no edge. Do not bet. Formula: f = (b×p − q) / b" },
                  { icon: "🛡️", title: "Never exceed 5%", body: "Cap every bet at 5% of your bankroll regardless of Kelly output — protects against model errors and variance." },
                ].map(item => (
                  <div key={item.title} style={{ display: "flex", gap: 12, padding: "12px 14px", background: "var(--bg3)", borderRadius: "var(--radius-sm)" }}>
                    <div style={{ fontSize: 18, flexShrink: 0 }}>{item.icon}</div>
                    <div>
                      <div style={{ fontFamily: "var(--font-display)", fontWeight: 600, fontSize: 13, marginBottom: 3 }}>{item.title}</div>
                      <div style={{ fontSize: 12, color: "var(--text3)", lineHeight: 1.5 }}>{item.body}</div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      {/* HISTORY */}
      {tab === "history" && (
        <div className="card">
          <div className="section-header">
            <div className="section-title">Bet History</div>
            <div style={{ fontSize: 12, color: "var(--text3)" }}>{history.length} bets</div>
          </div>
          {history.length === 0 ? (
            <div className="empty-state">
              <div className="empty-icon">📋</div>
              <p>No bets logged yet. Start tracking from the CLV Tracker.</p>
            </div>
          ) : isMobile ? (
            <div>
              {history.map(b => (
                <div key={b._id} style={{ padding: '12px 16px', borderBottom: '1px solid var(--border)', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: 13, fontWeight: 500, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{b.homeTeam} vs {b.awayTeam}</div>
                    <div style={{ fontSize: 11, color: 'var(--text3)', marginTop: 2 }}>{b.selection} · {b.bookmakeName} · {b.oddsAtPlacement}</div>
                    <div style={{ fontSize: 11, color: 'var(--text3)', marginTop: 2 }}>Stake: P{b.stake}</div>
                  </div>
                  <div style={{ textAlign: 'right', flexShrink: 0, marginLeft: 12 }}>
                    <div style={{ fontFamily: 'var(--font-display)', fontWeight: 700, fontSize: 14, color: b.profitLoss >= 0 ? 'var(--success)' : 'var(--danger)' }}>
                      {b.profitLoss != null ? `${b.profitLoss >= 0 ? "+" : ""}P${b.profitLoss?.toFixed(2)}` : "—"}
                    </div>
                    <ResultPill result={b.result} />
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <table className="data-table">
              <thead>
                <tr>
                  <th>Match</th><th>Selection</th><th>Book</th>
                  <th style={{ textAlign: "right" }}>Odds</th>
                  <th style={{ textAlign: "right" }}>Stake</th>
                  <th style={{ textAlign: "right" }}>P&L</th>
                  <th style={{ textAlign: "right" }}>CLV</th>
                  <th style={{ textAlign: "right" }}>Result</th>
                </tr>
              </thead>
              <tbody>
                {history.map(b => (
                  <tr key={b._id}>
                    <td style={{ fontSize: 12 }}>{b.homeTeam} vs {b.awayTeam}</td>
                    <td style={{ fontSize: 12 }}>{b.selection}</td>
                    <td><span className="pill pill-blue" style={{ fontSize: 10 }}>{b.bookmakeName}</span></td>
                    <td style={{ textAlign: "right", fontFamily: "var(--font-display)", fontWeight: 600 }}>{b.oddsAtPlacement}</td>
                    <td style={{ textAlign: "right", fontFamily: "var(--font-display)" }}>P{b.stake}</td>
                    <td style={{ textAlign: "right", fontFamily: "var(--font-display)", fontWeight: 700, color: b.profitLoss >= 0 ? "var(--success)" : "var(--danger)" }}>
                      {b.profitLoss != null ? `${b.profitLoss >= 0 ? "+" : ""}P${b.profitLoss?.toFixed(2)}` : "—"}
                    </td>
                    <td style={{ textAlign: "right", fontFamily: "var(--font-display)", color: b.clvPct >= 0 ? "var(--success)" : "var(--danger)" }}>
                      {b.clvPct != null ? `${b.clvPct >= 0 ? "+" : ""}${b.clvPct}%` : "—"}
                    </td>
                    <td style={{ textAlign: "right" }}><ResultPill result={b.result} /></td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      )}
    </div>
  );
}